import API_URL from '../config';

/**
 * Análise Avulsa V1 — chamadas do fluxo pay-per-use público (/avulsa).
 *
 * Usa fetch PURO (não o axios `api`) de propósito, como o supplierPublicService:
 * o fluxo avulsa vive numa rota pública e NÃO deve carregar o interceptor de
 * auth do app (Bearer do tenant logado / refresh-em-401). O token da avulsa é
 * próprio, guardado numa chave DEDICADA ('avulsa_token') pra não colidir com o
 * localStorage.token do app logado (visitar '/' não vira "logado como avulsa").
 *
 * Erros: lança Error com .status pro componente mapear a mensagem.
 */

const TOKEN_KEY = 'avulsa_token';
const PURCHASE_KEY = 'avulsa_purchase_id';
// numero_nc é cacheado do /nc/fill: o workspace-status devolve só nc_id, então
// sem cache o número "some" ao recarregar a página (PR4b → PR4c).
const NUMERO_KEY = 'avulsa_numero_nc';
// chave de idempotência ESTÁVEL por compra: re-clicar "pagar" reusa a mesma key
// → o backend não duplica a cobrança (idempotency_key UNIQUE).
const IDEMPOTENCY_KEY = 'avulsa_idempotency_key';
// atribuição de anúncio (gclid + utm_*) capturada do ?query no load da
// landing/app → persistida → enviada no signup → gravada na AvulsaPurchase
// (export CSV de conversão offline). Sobrevive ao salto de domínio (a LP anexa
// os params ao href do app); aqui só lemos da nossa própria URL.
const ATTRIBUTION_KEY = 'avulsa_attribution';
const ATTRIBUTION_FIELDS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function setSession(data) {
  if (data && data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data && data.purchase_id != null) localStorage.setItem(PURCHASE_KEY, String(data.purchase_id));
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PURCHASE_KEY);
  localStorage.removeItem(NUMERO_KEY);
  localStorage.removeItem(IDEMPOTENCY_KEY);
  localStorage.removeItem('avulsa_fill_draft'); // rascunho do fill (AvulsaFillForm)
}

// Limpa SÓ os caches POR-COMPRA (mantém token + atribuição): usado na recompra,
// pra a nova cobrança não reusar a chave de idempotência da compra anterior
// (a key é UNIQUE no backend) nem mostrar o número/rascunho da NC velha.
function clearPurchaseScopedCache() {
  localStorage.removeItem(NUMERO_KEY);
  localStorage.removeItem(IDEMPOTENCY_KEY);
  localStorage.removeItem('avulsa_fill_draft');
}

function rememberNumeroNc(numeroNc) {
  if (numeroNc) localStorage.setItem(NUMERO_KEY, String(numeroNc));
}

function getNumeroNc() {
  return localStorage.getItem(NUMERO_KEY);
}

// Gera (1×) e reusa uma chave de idempotência pra a cobrança desta compra.
function getChargeIdempotencyKey() {
  let k = localStorage.getItem(IDEMPOTENCY_KEY);
  if (!k) {
    k = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `avulsa-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(IDEMPOTENCY_KEY, k);
  }
  return k;
}

// Lê gclid/utm_* da URL atual e MERGE na atribuição persistida (não sobrescreve
// um valor já capturado com vazio — o 1º toque vence). Best-effort: nunca quebra.
function captureAttribution() {
  try {
    const qs = new URLSearchParams(window.location.search || '');
    const cur = getAttribution();
    let changed = false;
    ATTRIBUTION_FIELDS.forEach((f) => {
      const v = (qs.get(f) || '').trim().slice(0, 255);
      if (v && !cur[f]) { cur[f] = v; changed = true; }
    });
    if (changed) localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(cur));
  } catch (e) { /* best-effort */ }
}

function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

function _authHeaders(extra = {}) {
  const t = getToken();
  return { ...(t ? { Authorization: `Bearer ${t}` } : {}), ...extra };
}

async function _handle(response) {
  if (response.ok) {
    // 204/sem body → {}
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  }
  let detail = '';
  try {
    const body = await response.json();
    detail = body.detail || '';
  } catch {
    // resposta sem body JSON
  }
  const err = new Error(detail || `HTTP ${response.status}`);
  err.status = response.status;
  throw err;
}

export const avulsaService = {
  getToken,
  setSession,
  clearSession,
  rememberNumeroNc,
  getNumeroNc,
  getChargeIdempotencyKey,
  captureAttribution,
  getAttribution,
  hasSession: () => !!getToken(),

  // POST público (form) — cria tenant avulsa + admin + consent + AvulsaPurchase.
  // Guarda o token retornado na chave dedicada e devolve {purchase_id, client_id}.
  // CNPJ foi DEFERIDO pro /charge (#287): o signup é só empresa+nome+email+senha
  // (menos fricção pro tráfego frio pago). Anexa a atribuição de anúncio
  // (gclid/utm_*) capturada no load → o backend grava na AvulsaPurchase.
  signup: async ({ nomeEmpresa, nome, email, senha, aceitoTermos }) => {
    const params = new URLSearchParams();
    params.append('nome_empresa', (nomeEmpresa || '').trim());
    params.append('nome', (nome || '').trim());
    params.append('email', (email || '').trim());
    params.append('senha', senha || '');
    params.append('aceito_termos', aceitoTermos ? 'true' : 'false');
    const attr = getAttribution();
    ATTRIBUTION_FIELDS.forEach((f) => { if (attr[f]) params.append(f, attr[f]); });
    const r = await fetch(`${API_URL}/api/avulsa/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params,
    });
    const data = await _handle(r);
    setSession(data);
    try { localStorage.removeItem(ATTRIBUTION_KEY); } catch (e) { /* best-effort */ }
    return data;
  },

  // POST (form, Bearer) — cria/atualiza a NC rascunho, SEM IA.
  fill: async (fields) => {
    const params = new URLSearchParams();
    Object.entries(fields || {}).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, v);
    });
    const r = await fetch(`${API_URL}/api/avulsa/nc/fill`, {
      method: 'POST',
      headers: _authHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      body: params,
    });
    return _handle(r);
  },

  // POST (multipart, Bearer) — extrai os campos do documento do cliente (a "isca").
  // Roda DEPOIS do cadastro (CNPJ + consent já dados) — IA barata de extração, ≠ a
  // Investigação (pós-pagamento). NÃO cria NC: devolve dados_extraidos pra pré-
  // preencher o fill. Travas server-side: 3×/compra + janela por IP + 10MB.
  // NÃO setamos Content-Type — o browser põe o boundary do multipart sozinho.
  importar: async (file) => {
    const fd = new FormData();
    fd.append('arquivo', file);
    const r = await fetch(`${API_URL}/api/avulsa/nc/importar`, {
      method: 'POST',
      headers: _authHeaders(),
      body: fd,
    });
    return _handle(r);
  },

  // POST (json, Bearer) — cria a cobrança (manual/Pix). Idempotente por key.
  // cnpj (#287) é enviado AQUI (deferido do signup): o backend o exige antes de
  // cobrar quando a conta ainda não tem um (razão social do relatório + dedup).
  // Mandamos só os dígitos.
  charge: async (idempotencyKey, cnpj) => {
    const body = { idempotency_key: idempotencyKey };
    const digits = (cnpj || '').replace(/\D/g, '');
    if (digits) body.cnpj = digits;
    const r = await fetch(`${API_URL}/api/avulsa/charge`, {
      method: 'POST',
      headers: _authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return _handle(r);
  },

  // GET (Bearer) — estado durável da compra (paid? frozen? status? reruns?).
  // Reconcilia analyzing→completed/failed no backend (compute-on-read).
  workspaceStatus: async () => {
    const r = await fetch(`${API_URL}/api/avulsa/workspace-status`, {
      method: 'GET',
      headers: _authHeaders(),
    });
    return _handle(r);
  },

  // GET (Bearer) — a NC analisada do próprio tenant (tenant-guarded; o avulsa é
  // dono da NC). Devolve relatorio_html (com o rodapé avulsa) + analise_v4.
  // O gating avulsa bloqueia só ESCRITA — LER o resultado é liberado.
  // #257: `secoes` opcional (array de chaves) → `?secoes=...` filtra o
  // relatorio_html no backend (report_service). Sem secoes = relatório completo.
  // Só afeta o relatorio_html; o analise_v4 volta sempre completo (o editor não muda).
  getNc: async (ncId, { secoes } = {}) => {
    const qs = (Array.isArray(secoes) && secoes.length)
      ? `?secoes=${encodeURIComponent(secoes.join(','))}`
      : '';
    const r = await fetch(`${API_URL}/api/nc/${ncId}${qs}`, {
      method: 'GET',
      headers: _authHeaders(),
    });
    return _handle(r);
  },

  // PUT (json, Bearer) — edição manual da análise pelo comprador avulsa (PR-B).
  // Espelha o body de ncService.editarAnalise; aponta pro endpoint avulsa
  // (PR-A: PUT /api/avulsa/nc/{ncId}/editar-analise), que reusa o mesmo
  // _editar_analise_v4_impl do app (o comprador é admin do tenant dono da NC).
  // O editor avulsa roda com allowIaRecalc=false → só edição limpa (sem IA);
  // os flags de reanálise vão sempre false e o backend ainda os rejeita (400),
  // mas mantemos a forma idêntica pra paridade total com o app.
  editarAnalise: async (ncId, edicoes, { override = false, reanalisarParcial = false, reanalisarPorques = false, confirmarReanalise = false, previewAceito = null, regenAcoes = false } = {}) => {
    const r = await fetch(`${API_URL}/api/avulsa/nc/${ncId}/editar-analise`, {
      method: 'PUT',
      headers: _authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        edicoes,
        override,
        reanalisar_parcial: reanalisarParcial,
        reanalisar_porques: reanalisarPorques,
        confirmar_reanalise: confirmarReanalise,
        preview_aceito: previewAceito,
        regen_acoes: regenAcoes,
      }),
    });
    return _handle(r);
  },

  // POST best-effort — registra um evento de funil do frontend (sink avulsa_events).
  // NUNCA bloqueia nem quebra a navegação: sem sessão → no-op; erro → silencioso.
  // keepalive garante o envio mesmo quando o clique navega pra fora (assinar).
  event: (eventType, metadata) => {
    try {
      if (!getToken()) return; // landing pré-signup não tem token → não emite
      fetch(`${API_URL}/api/avulsa/event`, {
        method: 'POST',
        headers: _authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ event_type: eventType, metadata: metadata || undefined }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) { /* best-effort */ }
  },

  // POST (Bearer) — re-dispara após falha técnica (grátis, não consome pool).
  retry: async () => {
    const r = await fetch(`${API_URL}/api/avulsa/nc/retry`, {
      method: 'POST',
      headers: _authHeaders(),
    });
    return _handle(r);
  },

  // POST (Bearer) — recalcula após melhorar o contexto (consome 1 do pool).
  rerun: async () => {
    const r = await fetch(`${API_URL}/api/avulsa/nc/rerun`, {
      method: 'POST',
      headers: _authHeaders(),
    });
    return _handle(r);
  },

  // POST (Bearer) — inicia uma NOVA análise pro mesmo tenant (#297). Só funciona
  // a partir de um estado terminal (completed/expired/refunded); senão o backend
  // 409. Em caso de sucesso, limpa os caches por-compra (chave de idempotência da
  // anterior, número/rascunho da NC velha) e atualiza o purchase_id → o funil
  // recomeça no fill (workspace-status volta a devolver nc_id=null).
  repurchase: async () => {
    const r = await fetch(`${API_URL}/api/avulsa/repurchase`, {
      method: 'POST',
      headers: _authHeaders(),
    });
    const data = await _handle(r);
    clearPurchaseScopedCache();
    if (data && data.purchase_id != null) localStorage.setItem(PURCHASE_KEY, String(data.purchase_id));
    return data;
  },
};

export default avulsaService;
